import SelectBuscable from '@/shared/components/SelectBuscable'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { adminPurchaseOrdersService } from '@/features/proveedores/api/adminPurchaseOrders.service';
import toast from 'react-hot-toast';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

interface Supplier {
  id: string;
  legalName: string;
  rfc: string;
  email: string;
  status: string;
  contactName: string;
  contactPhone: string;
  created_at: string;
}

interface AssignSupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  orderId: string;
  orderNumber: string;
}

export const AssignSupplierModal: React.FC<AssignSupplierModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  orderId,
  orderNumber,
}) => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadSuppliers();
    }
  }, [isOpen]);

  const loadSuppliers = async () => {
    setIsLoading(true);
    try {
      // El token lo pone el cliente HTTP; ya no hay que leerlo aqui.
      const response = await adminPurchaseOrdersService.getSuppliers();
      const lista = (response as { data?: Supplier[] })?.data ?? [];
      const activeSuppliers = lista.filter((s: Supplier) => s.status === 'approved');
      setSuppliers(activeSuppliers);
    } catch {
      toast.error('Error al cargar proveedores');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedSupplierId) return;

    setIsSubmitting(true);
    try {
      await adminPurchaseOrdersService.assignSupplier(orderId, selectedSupplierId);
      toast.success('Proveedor asignado exitosamente');
      onSuccess();
      onClose();
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      toast.error(error.response?.data?.message || 'Error al asignar proveedor');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <CapaDeModal>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Asignar Proveedor
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Orden: <span className="font-semibold text-gray-900 dark:text-white">{orderNumber}</span>
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Seleccionar Proveedor *
            </label>
            {isLoading ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">Cargando proveedores...</div>
            ) : (
              <SelectBuscable
                variante="formulario"
                value={selectedSupplierId}
                onChange={setSelectedSupplierId}
                disabled={isSubmitting}
                placeholder="Seleccione un proveedor"
                placeholderDeBusqueda="Buscar proveedor…"
                opciones={suppliers.map((supplier) => ({
                  value: String(supplier.id),
                  label: `${supplier.legalName} - ${supplier.rfc}`,
                }))}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className={estiloDeBoton('secundario')}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedSupplierId || isSubmitting}
            className={estiloDeBoton('primario')}
          >
            {isSubmitting ? 'Asignando...' : 'Asignar'}
          </button>
        </div>
      </div>
    </CapaDeModal>
  );
};
